import { OMY_SETTING } from "../OmyConst";
import Omy from "../Omy";
import OMap from "../utils/OMap";
import { Howl } from "howler";
import OMath from "../utils/OMath";
import StringUtils from "../utils/StringUtils";

/**
 * @private
 * @enum {string}
 */

const FORMAT = {
  JSON: ".json",
  MP3: ".mp3",
  OGG: ".ogg",
  WEBM: ".webm",
  WEBP: ".webp",
  PNG: ".png",
  JPEG: ".jpeg",
  JPG: ".jpg",
  FNT: ".fnt",
  XML: ".xml"
};

export default class AssetsManager extends PIXI.utils.EventEmitter {
  constructor() {
    super();
    /**
     * @type {PIXI.Loader}
     */
    this._loader = Omy.loader;

    this._loadProgress = 0;
    /**
     * @desc Flag is any error happens.
     * @type {boolean}
     * @private
     */
    this._isError = false;

    this._loader.onComplete.add(this._onCompleteHandler, this);
    this._loader.onLoad.add(this._onLoadResourceComplete, this);
    this._loader.onError.add(this._onErrorResourceLoad, this);
    this._loader.onProgress.add(this._loadingProgress, this);
    this._loader.pre(this._preResource.bind(this));

    /** @type {OMap} */
    this._locMap = new OMap();
    /** @type {OMap} */
    this._bitmapFontDyMap = new OMap();
    /** @type {OMap} */
    this._bitmapFontDxMap = new OMap();
    /** @type {OMap} */
    this._lineHeightMap = new OMap();
    /** @type {OMap} */
    this._bFontCustomMap = new OMap();

    /** @type {OMap} */
    this._animMap = new OMap();
    /** @type {OMap} */
    this._spineMap = new OMap();
    this._audioCount = 0;
    this._audioTotal = 0;
    this._isLoad = false;

    this._textures = {};
    this._bitmapFontsNames = [];
    this._isSettingLoaded = false;
    this._animationsListNames = [];
    this._spineListNames = [];
    this._resolutionConfig = null;
    this._resolution = "";
    this._loadFilesConfig = {};
    this.LOAD_TYPE_IMG = "img";
    this.LOAD_TYPE_FONT = "font";
    this.LOAD_TYPE_ATLAS = "atlas";
    this.LOAD_TYPE_SPINE = "spine";
  }

  /**     * @public     */
  startLoad() {
    if (arguments.length) {
      console.error("assets is emitter. Pls use \"complete\", \"error\",  \"progress\" events!");
    }
    this._isError = false;
    this._isLoad = true;
    this._loadProgress = 0;

    this._loader.load();
    if (this._bitmapFontsNames.length) {
      Omy.log("BitmapFont names: ", this._bitmapFontsNames);
    }
  }

  /** @private */
  _loadingProgress(loader, resource) {
    this.emit("progress", ((this._isError) ? this._loadProgress : this._loader.progress));
  }

  _onLoadResourceComplete(loader, resource) {
  }

  _onErrorResourceLoad(error, loader, resource) {
    this._catchErrorLoad(error, loader, resource);
  }

  /** @private */
  _catchErrorLoad(error, loader, resource) {
    if (!this._isError) {
      this._isError = true;
      this._loadProgress = this._loader.progress;
      for (let resourcesKey in this._loader.resources) {
        if (this._loader.resources[resourcesKey].isLoading)
          this._loader.resources[resourcesKey].abort();
      }
      this._loader.reset();
      this.emit("error");
    }
  }

  /** @private */
  _onCompleteLoadAssets(resource) {
    if (!this._isError && resource.error) {
      this._catchErrorLoad(resource.error, this._loader, resource);
    }
  }

  /**     * @private     */
  _preResource(resource, next) {
    if (this._isError) {
      resource.abort();
      return;
    }
    resource.onComplete.once(this._onCompleteLoadAssets, this);

    if (resource.loadType === PIXI.LoaderResource.LOAD_TYPE.XHR) {
      resource.onComplete.once(function(res) {
        // console.warn('on JSON', res);
        if (res.name === "setting") this._isSettingLoaded = true;

        if (res.data && res.data.meta) {
          for (let framesKey in res.data.frames) {
            this._textures[framesKey] = res.name;
          }
        }
        if (res.name.split("@").length > 1) {
          if (res.data && res.data.meta)
            res.data.meta.scale = String(res.name.split("@")[1]);
        }
        if (res.extension === "fnt") {
          const dataString = res.data;
          if (dataString.indexOf("info face=\"" + res.name + "\"") === -1) {
            const splitData = dataString.split("\"");
            for (let i = 0; i < splitData.length; i++) {
              if (splitData[i].indexOf("face=") !== -1) {
                res.data = StringUtils.replaceAll(dataString, "info face=\"" + splitData[i + 1] + "\"",
                  "info face=\"" + res.name + "\"");
                break;
              }
            }
          }
          if (this._lineHeightMap.has(res.name)) {
            const dataString2 = res.data;
            const splitData = dataString2.split("\"");
            for (let i = 0; i < splitData.length; i++) {
              if (splitData[i].indexOf("lineHeight=") !== -1) {
                res.data = StringUtils.replaceAll(dataString2, "lineHeight=\"" + splitData[i + 1] + "\"",
                  "lineHeight=\"" + String(Number(splitData[i + 1]) + this._lineHeightMap.get(res.name)) + "\"");
                break;
              }
            }
          }
        }

        if (this._audioSpriteName === res.name) {
          if (!res.data) {
            console.error("catch null audiosprite data", this._audioSpriteName, this._audioSpriteURL);
            return;
          }
          const audioNames = [];
          for (let spriteKey in res.data.sprite) {
            audioNames.push(spriteKey);
          }
          this._addAudioSprite(res.data.sprite, audioNames);
          Omy.log("sounds names: ", audioNames);
        }
      }, this);
    } else if (resource.loadType === PIXI.LoaderResource.LOAD_TYPE.IMAGE) {
      let urlRes = resource.url;
      // console.log('old urlRes', urlRes);
      let indexDelete = urlRes.indexOf("." + String(resource.extension));
      let url = urlRes.slice(0, indexDelete);
      const isHaveWebp = this._isHaveWebp(resource.name);
      if (Omy.browser.isWebpSupported && OMY_SETTING.WEBP_SUPPORT && isHaveWebp) {
        urlRes = url + FORMAT.WEBP;
      }
      // console.log('new urlRes', urlRes);
      resource.url = urlRes;
    }

    if (resource.url.indexOf("tournaments") > 0)
      resource.url += Omy.URLCacheTournament;
    else
      resource.url += Omy.URLCache;
    next();
  }

  /**     * @private     */
  _onCompleteHandler() {
    if (this._isError) return;
    if (this._animationsListNames.length) {
      for (let i = 0; i < this._animationsListNames.length; i++) {
        let fileName = this._animationsListNames[i];
        let res = Omy.resources[fileName];
        if (res.data.meta.related_multi_packs?.length) {
          let effectsDataList = this._copyAnimationsFrom(null, res);
          let texturesDataList = this._copyTexturesFrom(null, res);
          for (let j = 0; j < res.data.meta.related_multi_packs.length; j++) {
            let name = res.data.meta.related_multi_packs[j].split(".json")[0];
            res = Omy.resources[name];
            if (res) {
              effectsDataList = this._copyAnimationsFrom(effectsDataList, res);
              texturesDataList = this._copyTexturesFrom(texturesDataList, res);
            }
          }
          for (let eKey in effectsDataList) {
            let aList = effectsDataList[eKey];
            let tList = [];
            for (let j = 0; j < aList.length; j++) tList.push(texturesDataList[aList[j]]);
            if (this._animMap.has(eKey)) this._animMap.set(eKey, this._animMap.get(eKey).concat(tList));
            else this._animMap.set(eKey, tList);
          }
        } else {
          if (res.spritesheet && res.spritesheet.animations) {
            let aData = res.spritesheet.animations;
            for (let aDataKey in aData) {
              if (this._animMap.has(aDataKey)) this._animMap.set(aDataKey, this._animMap.get(aDataKey).concat(aData[aDataKey]));
              else this._animMap.set(aDataKey, aData[aDataKey]);
            }
          }
        }
      }
      this._animationsListNames.length = 0;
      Omy.log("animation names: ", this._animMap.names(), this._animMap);
    }

    if (this._spineListNames.length) {
      Omy.groupCollapsed("spine animations");
      for (let i = 0; i < this._spineListNames.length; i++) {
        const spineName = this._spineListNames[i];
        const spineMapData = {};
        this._spineMap.set(spineName, spineMapData);
        Omy.groupCollapsed("animation name:", spineName);
        const data = this.getSpineData(spineName);
        if (data) {
          Omy.info("x:", data.x);
          Omy.info("y:", data.y);
          Omy.info("width:", data.width);
          Omy.info("width:", data.height);
          Omy.info("version:", data.version);

          spineMapData["animations"] = {};
          spineMapData["animationNames"] = [];
          Omy.group("animations list");
          for (let j = 0; j < data.animations.length; j++) {
            spineMapData["animationNames"].push(data.animations[j].name);
            spineMapData["animations"][data.animations[j].name] = data.animations[j].duration;
            Omy.info(
              "name:", data.animations[j].name,
              "time:", OMath.roundNumber(data.animations[j].duration, 100)
            );
          }
          Omy.groupEnd();

          spineMapData["skins"] = [];
          Omy.groupCollapsed("skins");
          for (let j = 0; j < data.skins.length; j++) {
            spineMapData["skins"].push(data.skins[j].name);
            Omy.info("name:", data.skins[j].name);
          }
          Omy.groupEnd();

          if (data.events) {
            spineMapData["events"] = [];
            Omy.groupCollapsed("events");
            for (let eventsKey in data.events) {
              spineMapData["events"].push(data.events[eventsKey].name);
              Omy.info("name:", data.events[eventsKey].name);
            }
            Omy.groupEnd();
          }
        }
        Omy.groupEnd();
      }
      Omy.groupEnd();
      Omy.log("spine animations names:\n", this._spineListNames);
    }

    if (this._isSettingLoaded) {
      this._isSettingLoaded = false;

      let setting = this.getJSON("setting");
      if (setting) {
        let animSetting = setting["spriteSheetEff"] || setting;
        this.addCustomEffect(animSetting);
      }
    }

    this._isLoad = false;
    if (this._audioCount < this._audioTotal) return;
    this.emit("complete");
  }

  /**     * @private     */
  _copyAnimationsFrom(data, res) {
    data = data || {};
    if (res.data.animations) {
      for (let dataKey in res.data.animations) {
        data[dataKey] = res.data.animations[dataKey];
      }
    }
    return data;
  }

  /**     * @private     */
  _copyTexturesFrom(data, res) {
    data = data || {};
    for (let dataKey in res.textures) {
      data[dataKey] = res.textures[dataKey];
    }
    return data;
  }

  /**
   * @desc Add middleware to loader.
   * @param {Function} middleware
   */
  addMiddleware(middleware) {
    this._loader.use(middleware);
  }

  addResolution(config) {
    this._resolutionConfig = config;
    let resolutionResult = null;
    let w = 0;
    let h = 0;
    if (window.screen.width < window.screen.height) {
      w = window.screen.height;
      h = window.screen.width;
    } else {
      w = window.screen.width;
      h = window.screen.height;
    }
    w *= window.devicePixelRatio;
    h *= window.devicePixelRatio;
    let scale = 0;

    for (let i = 0; i < this._resolutionConfig.length; i++) {
      const screen = this._resolutionConfig[i]["size"];
      scale = Math.max(w / screen[0], h / screen[1]);
      if (scale <= 1) {
        resolutionResult = this._resolutionConfig[i]["resolution"];
        break;
      }
    }
    if (!resolutionResult) {
      Omy.info("cannot check resolution. set by def max");
      this._resolution = this._resolutionConfig[this._resolutionConfig.length - 1]["resolution"];
    } else {
      this._resolution = resolutionResult;
    }
    Omy.log("game resolution", this._resolution);
  }

  /**     * @public     */
  loadJson(nameRes, urlRes) {
    this._addToLoader(nameRes, urlRes + nameRes + ".json");
  }

  /**     * @public     */
  loadLocal(nameRes, urlRes) {
    this._addToLoader(nameRes, urlRes);
  }

  /**     * @public     */
  loadImage(nameRes, urlRes, loadData = null) {
    if (loadData) {
      // OMY.Omy.warn("add load Img:", nameRes);
      if (!this._loadFilesConfig[this.LOAD_TYPE_IMG]) this._loadFilesConfig[this.LOAD_TYPE_IMG] = {};
      this._loadFilesConfig[this.LOAD_TYPE_IMG][nameRes] = loadData;
    }
    const locMap = loadData?.locMap;
    if (locMap) {
      this._locMap.set(nameRes, locMap);
      locMap["loadInfo"] = { "loader": "loadImage", "urlRes": urlRes };
      for (let locMapKey in locMap) {
        if (locMapKey.indexOf(Omy.language) !== -1) {
          nameRes = locMap[locMapKey];
          break;
        }
      }
    }
    this._addToLoader(nameRes, urlRes + nameRes + ((loadData && loadData["exc"]) ? loadData["exc"] : ""));
  }

  /**     * @public     */
  loadAtlas(nameRes, urlRes, loadData = null) {
    if (loadData) {
      if (!this._loadFilesConfig[this.LOAD_TYPE_ATLAS]) this._loadFilesConfig[this.LOAD_TYPE_ATLAS] = {};
      let configName = nameRes;
      if (nameRes.indexOf("-") > -1) {
        configName = configName.split("-")[0];
      }
      // OMY.Omy.warn("add load atlas:", configName);
      this._loadFilesConfig[this.LOAD_TYPE_ATLAS][configName] = loadData;
    }
    const locMap = loadData?.locMap;
    if (locMap) {
      this._locMap.set(nameRes, locMap);
      locMap["loadInfo"] = { "loader": "loadAtlas", "urlRes": urlRes };
      for (let locMapKey in locMap) {
        if (locMapKey.indexOf(Omy.language) !== -1) {
          nameRes = locMap[locMapKey];
          break;
        }
      }
    }
    if (loadData && loadData.hasOwnProperty("resolution") && loadData["resolution"]) {
      if (!this._resolutionConfig) console.error("pls. Add resolutionConfig. use addResolution method");
      nameRes += this._resolution;
      if (locMap) locMap["loadInfo"]["resolution"] = this._resolution;
    }
    this._addToLoader(nameRes, urlRes + nameRes, FORMAT.JSON);
  }

  /**     * @public     */
  loadAnimationAtlas(nameRes, urlRes, loadData = null) {
    if (loadData) {
      if (!this._loadFilesConfig[this.LOAD_TYPE_ATLAS]) this._loadFilesConfig[this.LOAD_TYPE_ATLAS] = {};
      let configName = nameRes;
      if (nameRes.indexOf("-") > -1) {
        configName = configName.split("-")[0];
      }
      this._loadFilesConfig[this.LOAD_TYPE_ATLAS][configName] = loadData;
    }
    this._animationsListNames.push(nameRes);
    this._addToLoader(nameRes, urlRes + nameRes, FORMAT.JSON);
  }

  /* @public     */
  loadSpine(nameRes, urlRes, loadData = null) {
    if (loadData) {
      // OMY.Omy.warn("add load spine:", nameRes);
      if (!this._loadFilesConfig[this.LOAD_TYPE_SPINE]) this._loadFilesConfig[this.LOAD_TYPE_SPINE] = {};
      this._loadFilesConfig[this.LOAD_TYPE_SPINE][nameRes] = loadData;
    }
    const url = urlRes;
    this._spineListNames.push(nameRes);
    this._addToLoader(nameRes, url + nameRes, FORMAT.JSON);
  }

  /**     * @public     */
  loadBitmapFont(nameRes, urlRes, loadData = null) {
    if (loadData) {
      // OMY.Omy.warn("add load font:", nameRes);
      if (!this._loadFilesConfig[this.LOAD_TYPE_FONT]) this._loadFilesConfig[this.LOAD_TYPE_FONT] = {};
      this._loadFilesConfig[this.LOAD_TYPE_FONT][nameRes] = loadData;
    }
    const locMap = loadData?.locMap;
    if (locMap) {
      this._locMap.set(nameRes, locMap);
      locMap["loadInfo"] = { "loader": "loadBitmapFont", "urlRes": urlRes };
      for (let locMapKey in locMap) {
        if (locMapKey.indexOf(Omy.language) !== -1) {
          nameRes = locMap[locMapKey];
          break;
        }
      }
    }
    if (loadData?.dxMap) this._bitmapFontDxMap.set(loadData.name, loadData.dxMap);
    if (loadData?.dyMap) this._bitmapFontDyMap.set(loadData.name, loadData.dyMap);
    const lineHeight = loadData?.lineHeight;
    if (lineHeight) {
      for (let lineHeightKey in lineHeight) {
        this._lineHeightMap.set(lineHeightKey, lineHeight[lineHeightKey]);
      }
    }
    this._bitmapFontsNames.push(nameRes);
    this._addToLoader(nameRes, urlRes + nameRes, (loadData?.xml) ? FORMAT.XML : FORMAT.FNT);
  }

  createBitmapFont(json) {
    for (let jsonKey in json) {
      if (!OMath.inArray(this._bitmapFontsNames, jsonKey)) {
        this._bitmapFontsNames.push(jsonKey);
        if (json[jsonKey].locMap) this._locMap.set(jsonKey, json[jsonKey].locMap);
        if (json[jsonKey].dxMap) this._bitmapFontDxMap.set(jsonKey, json[jsonKey].dxMap);
        if (json[jsonKey].dyMap) this._bitmapFontDyMap.set(jsonKey, json[jsonKey].dyMap);
        const char = json[jsonKey].chars;
        json[jsonKey].chars = "";
        for (let i = 0; i < char.length; i++) {
          json[jsonKey].chars += char.charAt(i) + " ";
        }
        PIXI.BitmapFont.from(jsonKey, json[jsonKey].style,
          {
            chars: json[jsonKey].chars,
            resolution: json[jsonKey].resolution || PIXI.settings.RESOLUTION,
            padding: (json[jsonKey].hasOwnProperty("padding")) ? json[jsonKey].padding : 4
          }
        );
      } else {
        OMY.Omy.error("this font", jsonKey, " exists on base. Pls add another name");
      }
    }
  }

  /** @public */
  loadAudioSprite(nameRes, urlRes) {
    if (this._audioSpriteName) {
      throw new Error("AudioSprite exists! Use only one audio sprite for project.");
      return;
    }
    this._audioSpriteName = nameRes;
    this._audioSpriteURL = urlRes;
    this._addToLoader(nameRes, urlRes, FORMAT.JSON);
  }

  /**     * @public     */
  loadAudio(nameRes, urlRes, volume = 1, type = "sound") {
    this._audioTotal++;
    let src;
    if (Omy.browser.isOggSupported && OMY_SETTING.OGG_SUPPORT) {
      src = [this._generateUrl(urlRes, FORMAT.OGG), this._generateUrl(urlRes, FORMAT.MP3)];
    } else {
      src = this._generateUrl(urlRes, FORMAT.MP3);
    }
    let sound = new Howl({
      src: src,
      autoplay: false,
      loop: type !== "sound",
      volume: volume,
      onload: this._onAudioLoad.bind(this),
      onloaderror: this._onAudioError.bind(this)
    });
    Omy.sound.add2Lib(type, nameRes, sound);
  }

  loadNeutrino(nameRes, urlRes, loadData = null) {
    this._loader.add(nameRes, urlRes + nameRes + ".js", Omy.game.neutrino.loadOptions);
  }

  initBundle(revoltName) {
    const json = this.getJSON(revoltName);
    Omy.initRevolt(revoltName, json);
  }

  /** @private */
  _addAudioSprite(sprite, audioNames) {
    this._audioTotal++;
    let src = [this._generateUrl(this._audioSpriteURL, FORMAT.WEBM + Omy.URLCache), this._generateUrl(this._audioSpriteURL, FORMAT.MP3 + Omy.URLCache)];
    let sound = new Howl({
      src: src,
      sprite: sprite,
      autoplay: false,
      onload: this._onAudioLoad.bind(this),
      onloaderror: this._onAudioSpriteError.bind(this)
    });
    Omy.sound.add2LibSprite(this._audioSpriteName, sound, audioNames);
  }

  /**
   * @desc Add asset for loading.
   * @method
   * @private
   * @param {string} name - Name of asset.
   * @param {string} url - Url of asset.
   * @param {FORMAT | string} [format = ""] - Format of asset.
   *
   */

  _addToLoader(name, url, format = "") {
    const loadUrl = this._generateUrl(url, format);
    this._loader.add({ name: name, url: loadUrl });
  }

  /**
   * @desc Generate url for asset
   * @method
   * @private
   * @param {string} url - Url of asset.
   * @param {FORMAT | string} [format = ""] - Format of asset.
   * @returns {string}
   */

  _generateUrl(url, format = "") {
    return url + format;
  }

  /**     * @private     */
  _onAudioLoad() {
    this._audioCount++;
    if (this._audioCount >= this._audioTotal && !this._isLoad) {
      this.emit("complete");
    }
  }

  /**     * @private     */
  _onAudioError() {
    this._onAudioLoad();
  }

  /**     * @private     */
  _onAudioSpriteError() {
    this.emit("error");
  }

  /**     * @public     */
  addCustomEffect(jsonData) {
    for (let i = 0; i < jsonData.length; i++) {
      let o = jsonData[i];
      if (this._animMap.has(o["cacheName"])) {
        continue;
      }
      let textures = [];
      let data;
      for (let j = 0; j < o['textures'].length; j++) {
        data = o["textures"][j];
        if (typeof data === "object" && !Array.isArray(data)) {
          if (data.name && !Omy.resources[data.name]) {
            console.warn("!OmyConst.resources[data.name]", data.name);
            continue;
          } else if (data.cacheName && !this._animMap.has(data.cacheName)) {
            console.warn("!this._animMap.has(data.cacheName)", data.cacheName);
            continue;
          }
          if (data.cacheName)
            textures = textures.concat(this._createAnimationFromCache(data));
          else if (data.frames)
            textures = textures.concat(this._createAnimationFromFrames(data));
          else if (data.animations || data.frameNames)
            textures = textures.concat(this._createAnimationFromNames(data));
        } else {
          if (!Omy.resources[data]) {
            console.warn("!OmyConst.resources[data]", data);
            continue;
          }
          textures = textures.concat(this._createAnimation(data));
        }
      }
      this._animMap.set(o["cacheName"], textures);
    }
  }

  getJSON(name) {
    if (!Omy.resources[name]) {
      Omy.warn("Cannot find json: " + name);
      return;
    }
    let json = Omy.resources[name].data;
    if (!json.isWasParse) {
      Omy.add.parseJson(json);
      json.isWasParse = true;
    }
    return json;
  }

  /**
   * @returns {string[]}
   */
  getAllTexturesNames() {
    return Object.keys(PIXI.utils.TextureCache)
      .filter(textureName => textureName.indexOf("/") === -1)
      .filter(textureName => textureName.toLowerCase().indexOf("assets") === -1);

  }

  /**
   * @returns {Array}
   */
  getAllAnimationNames() {
    return this._animMap.names();
  }

  /**
   * TODO:
   * @returns {string[]}
   */
  getAllBitmapFontsNames() {
    return this._bitmapFontsNames;
  }

  generateTextures(jsonData) {
    for (let jsonDataKey in jsonData) {
      Omy.add.generateTextureFrom(Omy.add.graphicJson(null, jsonData[jsonDataKey]), jsonData[jsonDataKey]["option"], jsonDataKey);
    }
  }

  getTexture(texture, atlas = null) {
    const textureCache = PIXI.utils.TextureCache;

    if (textureCache[texture]) {
      return PIXI.Texture.from(texture);
    }
    atlas = atlas || this._textures[texture];
    let nameSourse = atlas || texture;

    if (!Omy.resources[nameSourse]) {
      Omy.warn("Cannot find texture or atlas: " + nameSourse);
      return Omy.add.generateTextureFrom(Omy.add.graphicRectTexture(50, 50, 1, 0x000000));
    }
    if (atlas && !Omy.resources[nameSourse].textures[texture]) {
      Omy.warn("Cannot find texture: " + texture + " in atlas:" + nameSourse);
      return Omy.add.generateTextureFrom(Omy.add.graphicRectTexture(50, 50, 1, 0x000000));
    }
    if (!atlas)
      return Omy.resources[nameSourse].texture;
    else
      return Omy.resources[nameSourse].textures[texture];
  }

  getAnimation(name) {
    if (!this._animMap.has(name)) {
      Omy.warn("Cannot find animation: " + name + " Please add to OMY.Omy.assets.addCustomEffect for use");
      console.log(this._animMap);
      return;
    }
    return this._animMap.get(name);
  }

  _createAnimation(name) {
    let resource = Omy.resources[name];
    if (!resource) {
      Omy.warn("Cannot find texture: " + name);
      return;
    }
    let textures = [];
    let _frameKeys = resource.spritesheet._frameKeys;
    let _textures = resource.spritesheet.textures;
    for (let i = 0; i < _frameKeys.length; i++) {
      textures.push(_textures[_frameKeys[i]]);
    }
    return textures;
  }

  _createAnimationFromFrames(data) {
    if (!Omy.resources[data.name]) {
      Omy.warn("Cannot find texture: " + data.name);
      return;
    }
    let textures = [];
    let frames = data.frames;
    let resource = Omy.resources[data.name];
    let _frameKeys = resource.spritesheet._frameKeys;
    let _textures = resource.textures;

    for (let i = 0; i < frames.length; i++) {
      let num = frames[i];
      if (num.split("..").length > 1) {
        let j = Number(num.split("..")[0]);
        let m = Number(num.split("..")[1]);
        if (j < m) {
          m++;
          for (j; j < m; j++) {
            if (!_textures[_frameKeys[j]]) continue;
            textures.push(_textures[_frameKeys[j]]);
          }
        } else {
          m--;
          for (j; j >= m; j--) {
            if (!_textures[_frameKeys[j]]) continue;
            textures.push(_textures[_frameKeys[j]]);
          }
        }
      } else {
        if (!_textures[_frameKeys[Number(num)]]) continue;
        textures.push(_textures[_frameKeys[Number(num)]]);
      }
    }
    return textures;
  }

  _createAnimationFromNames(data) {
    if (!Omy.resources[data.name]) {
      Omy.warn("Cannot find texture: " + data.name);
      return;
    }
    let textures = [];
    let frameNames = data.frameNames;
    let animations = data.animations;
    let resource = Omy.resources[data.name];
    if (frameNames) {
      let _textures = resource.textures;

      for (let i = 0; i < frameNames.length; i++) {
        let nameFrame = frameNames[i];

        if (!_textures[nameFrame]) continue;
        textures.push(_textures[nameFrame]);
      }
    } else {
      textures = resource.spritesheet.animations[animations];
    }

    return textures;
  }

  _createAnimationFromCache(data) {
    if (!this._animMap.has(data.cacheName)) {
      Omy.warn("Cannot find animation: " + data.cacheName);
      return;
    }
    let textures = [];
    let frames = data.frames;
    let textureList = this._animMap.get(data.cacheName);

    if (frames) {
      for (let i = 0; i < frames.length; i++) {
        let num = frames[i];
        if (num.split("..").length > 1) {
          let j = Number(num.split("..")[0]);
          let m = Number(num.split("..")[1]);
          if (j < m) {
            m++;
            for (j; j < m; j++) {
              if (!textureList[j]) continue;
              textures.push(textureList[j]);
            }
          } else {
            m--;
            for (j; j >= m; j--) {
              if (!textureList[j]) continue;
              textures.push(textureList[j]);
            }
          }
        } else {
          if (!textureList[Number(num)]) continue;
          textures.push(textureList[Number(num)]);
        }
      }
    } else {
      textures = textureList.concat();
    }

    return textures;
  }

  getSpineData(name) {
    if (!Omy.resources[name]) {
      Omy.warn("Cannot find spineData: " + name);
      return;
    }

    return Omy.resources[name].spineData;
  }

  /**
   * @returns {Array}
   */
  getAllSpineNames() {
    let result = [];
    for (let name in Omy.resources) {
      if (Omy.resources[name].spineData) {
        result.push(name);
      }
    }
    return result;
  }

  getSpriteSheet(name) {
    if (!Omy.resources[name]) {
      Omy.warn("Cannot find SpriteSheet: " + name);
      return;
    }

    return Omy.resources[name].spritesheet;
  }

  /**
   * @public
   * @param {function}onComplete
   */
  cacheAnimations(onComplete = null) {
    Boolean(onComplete) && Omy.add.timer(0.5, onComplete);
    let container;
    let listNames = this._animMap.names();
    Omy.log("cache Animations: " + "", listNames);
    let json = {
      "cacheName": "symb_5", "nameAnimation": "symb_5",
      "type": "actor", "fps": 30, "speed": 1
    };
    const total = 5;
    for (let i = 0; i < listNames.length; i++) {
      if (!container) {
        container = Omy.add.container();
        Omy.stage.addChildAt(container, 0);
      }
      // let buffer = [];
      for (let j = 0; j < total; j++) {
        json.cacheName = listNames[i];
        json.nameAnimation = listNames[i];
        let a = Omy.add.actorJson(null, json);
        a.x = 5 * Omy.WIDTH + a.width;
        a.y = 5 * Omy.HEIGHT + a.height;
        container.addChild(a);
        a.removeOnEnd = true;
        // buffer.push(Omy.add.actorJson(null, json));
      }
      // for (let j = 0; j < buffer.length; j++) {
      //     buffer[j].kill();
      // }
      // buffer.length = 0;
    }

    if (OMY_SETTING.CACHE_SPINE_ON_START) {
      json = {
        "textures": "symb_5", "custom_a_name": "symb_5",
        "type": "actorSpine", "speed": 1
      };
      if (!container) {
        container = Omy.add.container(Omy.stage);
        Omy.stage.addChildAt(container, 0);
      }
      for (let i = 0; i < this._spineListNames.length; i++) {
        const spineData = this._spineMap.get(this._spineListNames[i]);

        json.textures = this._spineListNames[i];
        json.custom_a_name = spineData["animationNames"][0];
        try {
          // OMY.Omy.log('animation data', this._spineListNames[i], spineData['animationNames'][0]);
          /** @type {OMY.OActorSpine} */
          let a = Omy.add.actorJson(null, json);
          // OMY.Omy.log('a', a);
          a.x = 5 * Omy.WIDTH + a.width;
          a.y = 5 * Omy.HEIGHT + a.height;
          a.play();
          container.addChild(a);
          a.addComplete(this._onRemoveCacheSpine, this, true);
        } catch (err) {
          console.warn("Cache spine actor warning:", "skeleton:", json.textures, "animation:", json.custom_a_name);
        }
      }
    }
  }

  /**     * @private     */
  _onRemoveCacheSpine(a) {
    a.stop();
    a.visible = false;
    a.alpha = 0;
    a = null;
  }

  clearAnimMap() {
    this._animMap.clear();
  }

  /**
   * @param{String} fontName - name of font for checking
   * @returns {String}
   */
  existsBitmapFont(fontName) {
    if (!OMath.inArray(this._bitmapFontsNames, fontName)) {
      Omy.warn("not exist this font name", fontName,
        "pls load or remove from bitmapFont. Set default:", this._bitmapFontsNames[0]);
      fontName = this._bitmapFontsNames[0];
    }
    return fontName;
  }

  /**
   * check exist localisation for this assets
   * @param {String}assetsName
   * @returns {String}
   */
  localAssets(assetsName) {
    return this._findExistFromMap(assetsName, this._locMap.get(assetsName));
  }

  /**
   * get dx for anchorX on some font and current language
   * @param {String}fontName
   * @returns {Number}
   */
  getBitmapFontDx(fontName) {
    return this._findExistFromMap(0, this._bitmapFontDxMap.get(fontName), this._locMap.get(fontName));
  }

  /**
   * get dx for anchorY on some font and current language
   * @param {String}fontName
   * @returns {Number}
   */
  getBitmapFontDy(fontName) {
    return this._findExistFromMap(0, this._bitmapFontDyMap.get(fontName), this._locMap.get(fontName));
  }

  /**
   * check assets for existing on locMap
   * @param {String}assetsName
   * @returns {Boolean}
   */
  isLocalAssets(assetsName) {
    return this._locMap.has(assetsName);
  }

  checkLocMap() {
    let names = this._locMap.names();
    for (let i = 0; i < names.length; i++) {
      const locData = this._locMap.get(names[i]);
      let locName = null;
      for (let locMapKey in locData) {
        if (locMapKey.indexOf(Omy.language) !== -1) {
          locName = locData[locMapKey];
          break;
        }
      }
      locName = locName || names[i];
      if (locData["loadInfo"] && locData["loadInfo"]["resolution"])
        locName += locData["loadInfo"]["resolution"];
      if (!Omy.resources[locName] && locData["loadInfo"]) {
        const loadMethod = locData["loadInfo"]["loader"];
        this[loadMethod](locName, locData["loadInfo"]["urlRes"]);
      }
    }
  }

  /**
   * @param {Object}locMapObject
   */
  addFontLocMap(locMapObject) {
    if (!locMapObject) return;
    for (let fontName in locMapObject) {
      if (!this._locMap.has(fontName)) {
        locMapObject[fontName]["loadInfo"] = null;
        this._locMap.set(fontName, locMapObject[fontName]);
      }
    }
  }

  /**     * @private     */
  _isHaveWebp(resImgName) {
    // OMY.Omy.info("check img:", resImgName);
    let loadConfig = null;
    let findName = null;
    if (resImgName.indexOf("_atlas_page_") > -1) {
      findName = resImgName.split("_atlas_page_")[0];
      loadConfig = this._loadFilesConfig[this.LOAD_TYPE_SPINE];
    } else if (resImgName.indexOf("_image") > -1) {
      findName = resImgName.split("@")[0];
      findName = findName.split("-")[0];
      loadConfig = this._loadFilesConfig[this.LOAD_TYPE_ATLAS];
    } else if (resImgName.indexOf("assets/font/") > -1) {
      findName = resImgName.split("/font/")[1];
      findName = findName.split(".png")[0];
      loadConfig = this._loadFilesConfig[this.LOAD_TYPE_FONT];
    } else {
      findName = resImgName;
      loadConfig = this._loadFilesConfig[this.LOAD_TYPE_IMG];
    }
    for (let loadFilesConfigKey in loadConfig) {
      if (findName === loadFilesConfigKey
        && loadConfig[loadFilesConfigKey].hasOwnProperty("noWebp") && loadConfig[loadFilesConfigKey]["noWebp"]) {
        // OMY.Omy.warn("no need to load webp");
        return false;
      }
    }
    !findName && OMY.Omy.error("cannot find:", resImgName);
    return true;
  }

  /**
   *  check, find and return element from mapData
   * @param {*}returnData
   * @param {Object}data
   * @param {Object}additionalData
   * @return {*}
   * @private
   */
  _findExistFromMap(returnData, data, additionalData = null) {
    if (!data)
      return returnData;
    for (let key in data) {
      if (key.indexOf(Omy.language) !== -1) {
        return data[key];
      }
    }
    if (data.hasOwnProperty("eng")) {
      if (additionalData) {
        for (let key in additionalData) {
          if (key.indexOf(Omy.language) !== -1) {
            return returnData;
          }
        }
      }
      return data["eng"];
    }
    return returnData;
  }

  /**
   * add new font chars to BitmapFont
   * @param {String}fontName
   * @param {String}chars
   * @param {Object}fontConfig
   */
  addChars2BitmapFont(fontName, chars, fontConfig) {
    if (!this._bFontCustomMap.has(fontName)) {
      this._bFontCustomMap.set(fontName, {
        fontName,
        chars,
        fontConfig
      });
    }
    fontName = this.localAssets(fontName);
    const bitmapfont = PIXI.BitmapFont.available[fontName];
    if (!bitmapfont)
      throw new Error(`Missing BitmapFont "${fontName}"`);
    // OMY.Omy.warn("work with font:", bitmapfont);
    const charsList = StringUtils.splitTextToCharacters(chars);
    let charsBitmapfont = PIXI.BitmapFont.available[fontName + "custom_chars"];
    if (!charsBitmapfont) {
      fontConfig.chars = "";
      fontConfig.style.fontSize = bitmapfont.size;
      for (let i = 0; i < charsList.length; i++) {
        fontConfig.chars += charsList[i] + " ";
      }
      PIXI.BitmapFont.from(fontName + "custom_chars", fontConfig.style,
        {
          chars: fontConfig.chars,
          resolution: fontConfig.resolution || PIXI.settings.RESOLUTION,
          padding: (fontConfig.hasOwnProperty("padding")) ? fontConfig.padding : 4
        }
      );
      charsBitmapfont = PIXI.BitmapFont.available[fontName + "custom_chars"];
      // OMY.Omy.warn("create font for need char", charsBitmapfont);
      for (let i = 0; i < charsList.length; i++) {
        const charId = StringUtils.extractCharCode(charsList[i]);
        const char = charsBitmapfont.chars[charId];
        if (char) {
          if (fontConfig.hasOwnProperty("kerning"))
            char["kerning"] = this._findExistFromMap(fontConfig["kerning"], fontConfig["kerningMap"]);
          if (fontConfig.hasOwnProperty("xOffset"))
            char["xOffset"] = this._findExistFromMap(fontConfig["xOffset"], fontConfig["xOffsetMap"]);
          if (fontConfig.hasOwnProperty("yOffset"))
            char["yOffset"] = this._findExistFromMap(fontConfig["yOffset"], fontConfig["yOffsetMap"]);
          if (fontConfig.hasOwnProperty("xAdvanceMap") && fontConfig["xAdvanceMap"][charsList[i]])
            char["xAdvance"] += fontConfig["xAdvanceMap"][charsList[i]];
          else if (fontConfig.hasOwnProperty("xAdvance"))
            char["xAdvance"] += fontConfig["xAdvance"];
          if (fontConfig.hasOwnProperty("xAdvanceCustom") && fontConfig["xAdvanceCustom"][charsList[i]])
            char["xAdvance"] = fontConfig["xAdvanceCustom"][charsList[i]];
        }
      }
    }
    for (let i = 0; i < charsList.length; i++) {
      const charId = StringUtils.extractCharCode(charsList[i]);
      if (!bitmapfont.chars[charId])
        bitmapfont.chars[charId] = charsBitmapfont.chars[charId];
    }
  }

  checkCustomChars() {
    const names = this._bFontCustomMap.names();
    for (let i = 0; i < names.length; i++) {
      const data = this._bFontCustomMap.get(names[i]);
      this.addChars2BitmapFont(data.fontName, data.chars, data.fontConfig);
    }
  }
}
